import {
  ClipboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Editable, ReactEditor, Slate, withReact } from 'slate-react';

import styled, { useTheme } from '@mint-lib/styled';
import Box from '@mui/material/Box';
import { SxProps } from '@mui/material/styles';
import DOMPurify from 'dompurify';
import { BaseEditor, createEditor, Editor, Node, Transforms } from 'slate';

import { getEditorStyles } from '../../common/TextArea/constants.js';
import { getBgColor, getBorder } from '../../common/TextArea/helpers.js';
import {
  countTextLength,
  getInitialValue,
  htmlToSlateNodes,
  renderEditorLeaf,
  type CustomSlateNode,
} from '../../common/TextArea/utils.jsx';
import FormHelperText from '../FormHelperText/FormHelperText.jsx';
import Icon from '../Icon/Icon.jsx';
import Truncation from '../Truncation/Truncation.jsx';
import Typography from '../Typography/Typography.jsx';
import FormatButtonControls from './FormatButtonControls.jsx';
import { LIST_TYPES, TextStyle } from './TextAreaWithControls.constants.jsx';
import {
  renderEditorElementExtended,
  serializeSlateToHtmlNew,
} from './TextAreaWithControls.utils.jsx';
import TextStyleSelect from './TextStyleSelect.jsx';

type CustomEditor = BaseEditor & ReactEditor;

export type TextAreaWithControlsProps = {
  helperText?: string;
  /** max length */
  maxLength?: number;
  /** If 'true' - disabled. */
  isDisabled?: boolean;
  /** If 'true', the label is displayed in an error state. */
  error?: boolean;
  /** Value of decimals limit  */
  warning?: boolean;
  /** readOnly  */
  isReadOnlyAccess?: boolean;
  /** The initial value of the text area. */
  value?: string;
  /** Description of the text area. */
  inputValue?: string;
  /** Height of the input area. */
  inputAreaHeight?: number;
  /** minHeight of the input area. */
  minHeight?: number;
  /** Function to handle saving the input value. */
  onSaveInputValue: (
    inputValue: string,
    serializeSlateToHtml: (node: CustomSlateNode) => string,
  ) => void;
  /** Custom styles */
  sx?: SxProps;
  /** Placeholder */
  placeholder?: string;
};

/**
 * Component for displaying a text area with formatting controls.
 *
 * @example
 * Basic usage:
 *
 * const handleSaveInputValueFunction = (inputValue, serializeSlateToHtml) => {
 *   const htmlValue = serializeSlateToHtml({ children: JSON.parse(inputValue) });
 * }
 *
 * <TextAreaWithControls
 *   helperText="Enter your text here"
 *   maxLength={100}
 *   onSaveInputValue={handleSaveInputValueFunction}
 *   value={inputValue}
 * />
 */

/**
 * @deprecated Use TextArea from @mint-lib/tw-components/patterns instead
 */
const TextAreaWithControls = ({
  inputValue = '',
  onSaveInputValue,
  helperText,
  maxLength,
  isReadOnlyAccess = false,
  isDisabled = false,
  error,
  warning,
  inputAreaHeight,
  minHeight = 72,
  placeholder,
  sx,
}: TextAreaWithControlsProps) => {
  const theme = useTheme();
  const maxLengthRef = useRef<HTMLSpanElement>(null);
  const [maxLengthWidth, setMaxLengthWidth] = useState<string | null>(null);
  const [value, setValue] = useState<Node[]>(getInitialValue(inputValue));
  const editor = useMemo(() => withReact(createEditor() as ReactEditor), []);
  const [isFocused, setIsFocused] = useState(false);
  const editorWrapperRef = useRef(null);

  useEffect(() => {
    if (maxLengthRef.current) {
      setMaxLengthWidth(maxLengthRef.current.clientWidth + 1 + 'px');
    }
  }, [editor, inputValue, maxLengthRef]);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter') {
        editor.insertSoftBreak();
      }
      event.stopPropagation();
    },
    [editor],
  );

  const preparedValue =
    typeof value === 'string'
      ? [
          {
            type: 'placeholder',
            children: [
              {
                text: value,
              },
            ],
          },
        ]
      : value;

  const length = useMemo(() => {
    return countTextLength(value);
  }, [value]);

  const toggleList = useCallback(
    (format: string) => () => {
      const isActive = isBlockActive(editor, format);
      const isList = LIST_TYPES.includes(format);

      Transforms.unwrapNodes(editor, {
        match: (n) => {
          const CustomSlateNode = n as CustomSlateNode;
          return (
            !!CustomSlateNode.type && LIST_TYPES.includes(CustomSlateNode.type)
          );
        },
        split: true,
      });
      if (isActive) {
        Transforms.setNodes(editor, {
          type: TextStyle.normal,
          children: [],
        });
      } else {
        Transforms.setNodes(editor, {
          type: isList ? 'list-item' : format,
          children: [],
        });
      }

      if (!isActive && isList) {
        const block = { type: format, children: [] };
        Transforms.wrapNodes(editor, block);
      }

      onSaveInputValue(
        JSON.stringify(editor.children),

        serializeSlateToHtmlNew,
      );
    },
    [editor, value],
  );

  const isBlockActive = useCallback(
    (editorProps: BaseEditor, format: string) => {
      const [match] = Editor.nodes(editorProps, {
        match: (n) => {
          const CustomSlateNode = n as CustomSlateNode;
          return CustomSlateNode.type === format;
        },
      });
      return !!match;
    },
    [],
  );

  const selectTextStyle = (style: string) => {
    toggleList(style)();
  };

  const onChange = (val: Node[]) => {
    setValue(val);
  };

  const onBlur = useCallback(() => {
    if (isDisabled) {
      return;
    }
    onSaveInputValue(JSON.stringify(value), serializeSlateToHtmlNew);
    setIsFocused(false);
  }, [value, isDisabled]);

  // We also can use editor.insertData instead of onPaste, but it has open issue with pasting
  // html layout and from time to time it may crash
  const onPaste = (
    event: ClipboardEvent<HTMLDivElement>,
    editor: CustomEditor,
  ) => {
    event.preventDefault();
    const { clipboardData } = event;
    const html = clipboardData?.getData('text/html');
    const sanitizedHtml = DOMPurify.sanitize(html ?? '');
    const parsed = new DOMParser().parseFromString(sanitizedHtml, 'text/html');
    const fragmentsRaw = htmlToSlateNodes(parsed.body);

    Transforms.insertFragment(editor, fragmentsRaw);
    return sanitizedHtml;
  };

  const focusEditor = () => {
    if (!isReadOnlyAccess) {
      // Store current scroll position
      const scrollPosition = globalThis.scrollY;
      ReactEditor.focus(editor);
      // Restore scroll position
      globalThis.scrollTo({
        top: scrollPosition,
        behavior: 'instant',
      });
    }
  };

  const onFocus = () => {
    if (isDisabled) {
      return;
    }
    setIsFocused(true);
  };

  const helperTextWidth = () => {
    if (maxLength) {
      return `calc(100% - ${maxLengthWidth})`;
    }
    if (!maxLength) {
      return `100%`;
    }
  };

  return (
    <Slate editor={editor} value={preparedValue} onChange={onChange}>
      <Box sx={sx}>
        {!isReadOnlyAccess && (
          <Box
            top="0"
            zIndex={1}
            display="flex"
            gap="8px"
            justifyContent="start"
            alignItems="center"
            height="32px"
            marginBottom={theme.spacing(0.5)}
          >
            <TextStyleSelect
              isDisabled={isDisabled}
              readOnly={isReadOnlyAccess}
              selectTextStyle={selectTextStyle}
            />
            <FormatButtonControls isDisabled={isDisabled} />
          </Box>
        )}
        <Box
          id="editorWrapper"
          border={getBorder(theme, isDisabled, isFocused, error, warning)}
          bgcolor={getBgColor(isDisabled, isReadOnlyAccess, theme)}
          borderRadius={theme.spacing(0.5)}
          display="flex"
          flexDirection="column"
          height="4.5rem"
          flex={1}
          padding={`${theme.spacing(1)} ${theme.spacing(2)}`}
          onKeyDown={onKeyDown}
          ref={editorWrapperRef}
          onClick={focusEditor}
          sx={{
            ...getEditorStyles({
              theme,
              isDisabled,
              isReadOnlyAccess,
              isFocused,
            }),
            minHeight: minHeight ? `${minHeight}px` : 'auto',
            height: inputAreaHeight ? `${inputAreaHeight}px` : 'auto',
          }}
        >
          <Editable
            data-testId="editor"
            placeholder={placeholder ?? 'Write your brief here'}
            renderElement={renderEditorElementExtended}
            renderLeaf={renderEditorLeaf}
            onFocus={onFocus}
            onBlur={onBlur}
            onPaste={(event) => onPaste(event, editor)}
            readOnly={isDisabled || isReadOnlyAccess}
          />
        </Box>
      </Box>
      <HelperText>
        {helperText && (
          <StyledFormHelperText
            sx={{ width: helperTextWidth() }}
            warning={warning}
            error={error}
            disabled={isDisabled}
          >
            {warning && !error && <Icon component="WarningAltFilled" />}
            {error && <Icon component="WarningFilled" />}
            <Truncation
              tooltipTitle={helperText}
              tooltipPlacement="bottom"
              variant="helperText01"
            >
              {helperText}
            </Truncation>
          </StyledFormHelperText>
        )}
        {maxLength && (
          <Typography
            ref={maxLengthRef}
            align="right"
            variant="helperText01"
            color={
              isDisabled
                ? theme.palette.action.disabled01
                : theme.palette.text.secondary
            }
            paddingTop="4px"
          >
            {length}/{maxLength}
          </Typography>
        )}
      </HelperText>
    </Slate>
  );
};

export default TextAreaWithControls;

const HelperText = styled(Box)`
  display: flex;
  justify-content: space-between;
  gap: 3px;
`;

const StyledFormHelperText = styled(FormHelperText, {
  shouldForwardProp: (prop) => prop !== 'warning',
})<{
  warning?: boolean;
}>(({ warning, theme }) => ({
  display: 'flex',
  gap: '5px',
  color: warning ? theme.palette.warning.main : '',
}));
